/**
 * BillingTestReport — Redacted Superbill Comparison Sheet
 *
 * Shows what ChartFlow would transmit to Trizetto (837P claim lines) vs.
 * what your current EMR billed. Filter by date range, provider, exclude
 * specific encounters. Export CSV or print.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import {
  Calendar, Download, Printer, RefreshCw, Filter, X, Check,
  ChevronDown, AlertTriangle, FileText, Loader2, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// ─── helpers ────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = () => {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

const EM_COLOR = {
  '99202': 'bg-sky-50 text-sky-700', '99203': 'bg-sky-50 text-sky-700',
  '99204': 'bg-sky-100 text-sky-800', '99205': 'bg-sky-200 text-sky-900',
  '99211': 'bg-slate-50 text-slate-600', '99212': 'bg-slate-50 text-slate-600',
  '99213': 'bg-emerald-50 text-emerald-700',
  '99214': 'bg-amber-50 text-amber-700',
  '99215': 'bg-orange-100 text-orange-800',
};

const emColor = (code) => EM_COLOR[code] || 'bg-slate-100 text-slate-700';

// ─── main component ──────────────────────────────────────────────────────────
export default function BillingTestReport() {
  const [startDate, setStartDate] = useState(thirtyDaysAgo());
  const [endDate, setEndDate]     = useState(today());
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [redacted, setRedacted]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [report, setReport]       = useState(null);
  const [excluded, setExcluded]   = useState(new Set());  // encounter IDs to exclude
  const [showFilters, setShowFilters] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    api.get('/providers').then(r => setProviders(r.data.providers || [])).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post('/billing/test-report', {
        start_date: startDate,
        end_date: endDate,
        provider_id: selectedProvider || undefined,
        exclude_encounter_ids: Array.from(excluded),
        redacted,
      });
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally { setLoading(false); }
  }, [startDate, endDate, selectedProvider, excluded, redacted]);

  useEffect(() => { loadReport(); }, []);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('prattos_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/billing/test-report/csv`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            start_date: startDate, end_date: endDate,
            provider_id: selectedProvider || undefined,
            exclude_encounter_ids: Array.from(excluded),
            redacted: true,
          }),
        }
      );
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `billing_test_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handlePrint = () => window.print();

  const toggleExclude = (id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const rows = report?.encounters || [];
  const summary = report?.summary || {};

  return (
    <div className="h-full flex flex-col bg-slate-50 print:bg-white">
      {/* ── Header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-slate-800">Billing Test Report</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Compare ChartFlow coding output (what would go to Trizetto) against your current EMR
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setRedacted(!redacted)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              title="Toggle patient name display">
              {redacted ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {redacted ? 'Show Names' : 'Redact Names'}
            </button>
            <button onClick={downloadCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <Button size="sm" onClick={loadReport} disabled={loading}
              className="gap-1.5 h-8 text-xs">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Run Report
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="text-xs px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="text-xs px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <select value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)}
            className="text-xs px-2 py-1 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary bg-white">
            <option value="">All Providers</option>
            {providers.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {excluded.size > 0 && (
            <span className="text-xs text-rose-600 font-medium flex items-center gap-1">
              <X className="w-3 h-3" /> {excluded.size} excluded
              <button onClick={() => setExcluded(new Set())} className="underline ml-1">clear</button>
            </span>
          )}
        </div>
      </div>

      {/* ── Summary bar ── */}
      {report && (
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-6 flex-wrap print:border-b-2 print:border-slate-800">
          <div className="text-center">
            <p className="text-xl font-bold text-slate-800">{summary.total_encounters}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Encounters</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">E/M Distribution</p>
            <div className="flex gap-1 flex-wrap">
              {Object.entries(summary.em_distribution || {}).sort().map(([code, cnt]) => (
                <span key={code} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${emColor(code)}`}>
                  {code}: {cnt}
                </span>
              ))}
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Avg E/M Level</p>
            <p className="text-sm font-bold text-slate-700">{summary.avg_em_level ?? '—'}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">J-Codes</p>
            <div className="flex gap-1 flex-wrap">
              {Object.entries(summary.j_code_summary || {}).map(([code, cnt]) => (
                <span key={code} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                  {code} ×{cnt}
                </span>
              ))}
              {!Object.keys(summary.j_code_summary || {}).length && (
                <span className="text-[10px] text-slate-400">None</span>
              )}
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">G2211</p>
            <p className="text-sm font-bold text-slate-700">{summary.g2211_count || 0}</p>
          </div>
          {(summary.encounters_missing_em_code || []).length > 0 && (
            <>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-50 border border-rose-200">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
                <span className="text-xs font-medium text-rose-700">
                  {summary.encounters_missing_em_code.length} missing E/M code
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto px-6 py-4" ref={printRef}>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <FileText className="w-10 h-10 opacity-30 mb-3" />
            <p className="text-sm">No finalized encounters in this date range</p>
            <p className="text-xs mt-1">Run some patient visits through ChartFlow and sign off to see billing data here</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Print header */}
            <div className="hidden print:block px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold">ChartFlow Billing Test Report</h2>
              <p className="text-sm text-slate-500">{startDate} through {endDate} · Generated {new Date().toLocaleDateString()}</p>
              <p className="text-xs text-slate-400 mt-1">CONFIDENTIAL — For internal coding comparison only</p>
            </div>

            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <th className="text-left px-3 py-2.5 font-semibold w-8 print:hidden"></th>
                  <th className="text-left px-3 py-2.5 font-semibold">Date</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Patient</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Provider</th>
                  <th className="text-left px-3 py-2.5 font-semibold">E/M Code</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Diagnosis (ICD-10)</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Additional CPT</th>
                  <th className="text-left px-3 py-2.5 font-semibold">J-Codes</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Mod</th>
                  <th className="text-center px-3 py-2.5 font-semibold">G2211</th>
                  <th className="text-left px-3 py-2.5 font-semibold print:hidden">Claim</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const isExcluded = excluded.has(row.encounter_id);
                  return (
                    <tr key={row.encounter_id}
                      className={`border-t border-slate-50 transition-colors
                        ${isExcluded ? 'opacity-40 bg-rose-50/30' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}
                        ${!row.em_code ? 'bg-amber-50/40' : ''}`}>
                      {/* Exclude checkbox */}
                      <td className="px-2 py-2 print:hidden">
                        <button onClick={() => toggleExclude(row.encounter_id)}
                          className={`w-4 h-4 rounded border flex items-center justify-center transition-colors
                            ${isExcluded ? 'bg-rose-500 border-rose-500 text-white' : 'border-slate-300 hover:border-slate-400'}`}
                          title={isExcluded ? 'Include in report' : 'Exclude from report / CSV'}>
                          {isExcluded && <X className="w-2.5 h-2.5" />}
                        </button>
                      </td>

                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.date}</td>

                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {redacted
                          ? row.patient_display
                          : <span className="flex flex-col">
                              <span>{row.patient_name_full || row.patient_display}</span>
                              <span className="text-[9px] text-slate-400 font-normal">{row.patient_display}</span>
                            </span>
                        }
                      </td>

                      <td className="px-3 py-2 text-slate-600 text-[10px]">
                        {row.provider_name?.split(' ').slice(-1)[0]}
                      </td>

                      {/* E/M Code */}
                      <td className="px-3 py-2">
                        {row.em_code ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${emColor(row.em_code)}`}>
                            {row.em_code}
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-600 font-medium flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Missing
                          </span>
                        )}
                        {row.em_label && (
                          <span className="block text-[9px] text-slate-400 mt-0.5">{row.em_label}</span>
                        )}
                      </td>

                      {/* Diagnosis */}
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(row.diagnosis_codes || []).slice(0, 3).map((dx, j) => (
                            <span key={j} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                              {dx}
                            </span>
                          ))}
                          {(row.diagnosis_codes || []).length > 3 && (
                            <span className="text-[9px] text-slate-400">+{row.diagnosis_codes.length - 3}</span>
                          )}
                        </div>
                      </td>

                      {/* Additional CPT */}
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(row.cpt_codes || []).filter(c => c.code !== 'G2211').map((c, j) => (
                            <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">
                              {c.code}{c.units > 1 ? ` ×${c.units}` : ''}{c.modifier ? ` [${c.modifier}]` : ''}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* J-Codes */}
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(row.j_codes || []).map((j, k) => (
                            <span key={k} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-mono font-bold">
                              {j.code}{j.units > 1 ? ` ×${j.units}` : ''}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Modifiers */}
                      <td className="px-3 py-2">
                        <div className="flex gap-0.5 flex-wrap">
                          {(row.modifiers || []).map((m, k) => (
                            <span key={k} className="text-[9px] font-mono px-1 py-0.5 rounded bg-slate-200 text-slate-700">{m}</span>
                          ))}
                        </div>
                      </td>

                      {/* G2211 */}
                      <td className="px-3 py-2 text-center">
                        {row.has_g2211 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-100 text-teal-600">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </td>

                      {/* Claim status */}
                      <td className="px-3 py-2 print:hidden">
                        {row.claim_status ? (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium
                            ${row.claim_status === 'accepted' ? 'bg-emerald-50 text-emerald-700' :
                              row.claim_status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                              'bg-slate-100 text-slate-500'}`}>
                            {row.claim_status}
                          </span>
                        ) : (
                          <span className="text-[9px] text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Footer totals */}
              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td colSpan={4} className="px-3 py-2 text-xs font-bold text-slate-600 print:col-span-3">
                      Totals — {rows.filter(r => !excluded.has(r.encounter_id)).length} encounters
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-0.5 flex-wrap">
                        {Object.entries(summary.em_distribution || {}).sort().map(([code, cnt]) => (
                          <span key={code} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${emColor(code)}`}>
                            {code}:{cnt}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td colSpan={2} className="px-3 py-2 text-[10px] text-slate-500">
                      {summary.total_line_items} total line items
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-0.5 flex-wrap">
                        {Object.entries(summary.j_code_summary || {}).map(([code, cnt]) => (
                          <span key={code} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                            {code}:{cnt}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td />
                    <td className="px-3 py-2 text-center text-[10px] font-bold text-teal-700">
                      {summary.g2211_count || 0}
                    </td>
                    <td className="print:hidden" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
